/**
 * PostsTab — unified Instagram posts / carousel studio inside the Image tab.
 *
 * A single post is just slide_count === 1 (no separate code path). The user
 * picks a subject, a slide count (1-10) or lets AI decide, a format preset, and
 * a full model-aware image config (provider/model/prompt/resolution/styles/
 * advanced) — the SAME config every other AI-image surface uses, rendered by
 * the shared <ImageGeneratorPanel>. The backend (CarouselOrchestrator) writes
 * one post-level caption + N slides, each an image with optional multi-font
 * text overlays.
 *
 * The progress bar + generic error panel + status polling all live in
 * <ImageGeneratorPanel>, so this tab and the YouTube thumbnail panel share the
 * exact same lifecycle UI (the carousel "Bug 0" fix lands in both at once).
 */

import { memo, useCallback, useMemo, useState } from 'react';
import { Images, Download, Copy, Check, Wand2, Loader2, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ImageGeneratorPanel } from '../ImageGeneratorPanel';
import type { ImageGenRunner } from '../ImageGeneratorPanel';
import { FreeOnDesktopBadge } from '../../common';
import { createDefaultImageConfig, DEFAULT_AI_IMAGE_PROVIDER } from '@/constants/imageSource';
import type { ImageGenerationConfig } from '@/types';
import { carouselAPI, resolveCarouselUrl, type CarouselStatus } from '@/services/carouselAPI';
import { apiFailureError } from '@/utils/httpUtils';
import { LLMModelSelector } from '@/components/selectors';
import { CompactSelect } from '@/components/common/CompactSelect';
import { useLLMModels } from '@/hooks/useLLMModels';
import { LLM_PROVIDERS, DEFAULT_VALUES } from '@/constants';

const FORMAT_PRESETS: ReadonlyArray<{
  key: string;
  labelKey: string;
  fallback: string;
  ratio: string;
}> = [
  { key: 'instagram_post', labelKey: 'image.posts.formatSquare', fallback: 'Square', ratio: '1:1' },
  {
    key: 'instagram_portrait',
    labelKey: 'image.posts.formatPortrait',
    fallback: 'Portrait',
    ratio: '4:5',
  },
  {
    key: 'instagram_story',
    labelKey: 'image.posts.formatStory',
    fallback: 'Story / Reel',
    ratio: '9:16',
  },
];

const MIN_SLIDES = 1;
const MAX_SLIDES = 10;
const POLL_INTERVAL_MS = 1500;

export const PostsTab = memo(function PostsTab() {
  const { t } = useTranslation();

  const [topic, setTopic] = useState('');
  const [slideCount, setSlideCount] = useState(5);
  const [formatPreset, setFormatPreset] = useState('instagram_post');
  // Full model-aware config (provider/model/prompt/resolution/styles/advanced).
  // The carousel orchestrator derives slide dimensions from format_preset, so we
  // hide the resolution selector here and seed the carousel default provider.
  const [config, setConfig] = useState<ImageGenerationConfig>(() =>
    createDefaultImageConfig({ provider: DEFAULT_AI_IMAGE_PROVIDER })
  );

  // Caption/plan LLM (writes the post caption + slide copy). Same provider +
  // model selector pattern as AudioConfigForm / dubbing's AdvancedSettingsSection:
  // a provider picker (LLM_PROVIDERS) feeding useLLMModels, which owns default
  // model selection/reconciliation — do not shadow it with local model state.
  const [llmProvider, setLlmProvider] = useState<string>(DEFAULT_VALUES.LLM_PROVIDER);
  const {
    availableModels: llmModels,
    modelsError: llmModelsError,
    selectedModel: llmModel,
    setSelectedModel: setLlmModel,
  } = useLLMModels(llmProvider, undefined, {
    context: 'chat',
    enabled: true,
  });

  const [isSuggesting, setIsSuggesting] = useState(false);
  const [outline, setOutline] = useState<string[]>([]);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [status, setStatus] = useState<CarouselStatus | null>(null);
  const [captionCopied, setCaptionCopied] = useState(false);

  const handleSuggestCount = useCallback(async () => {
    if (!topic.trim()) return;
    setIsSuggesting(true);
    setSuggestError(null);
    const resp = await carouselAPI.suggestCount(topic.trim());
    setIsSuggesting(false);
    if (resp.success && resp.data) {
      setSlideCount(Math.min(MAX_SLIDES, Math.max(MIN_SLIDES, resp.data.slide_count)));
      setOutline(resp.data.outline ?? []);
    } else {
      setSuggestError(
        resp.error ??
          t('image.posts.suggestError', { defaultValue: 'Failed to suggest a slide count' })
      );
    }
  }, [topic, t]);

  // Runner: carousel POST returns a carousel_id, then we poll /status. The
  // generic error message (e.g. "Fooocus AI is not running…") propagated by
  // carouselAPI via extractApiErrorMessage flows straight into the panel.
  const runner = useMemo<ImageGenRunner<CarouselStatus>>(
    () => ({
      pollIntervalMs: POLL_INTERVAL_MS,
      start: async (cfg) => {
        setStatus(null);
        const resp = await carouselAPI.generate({
          topic: topic.trim(),
          slideCount,
          formatPreset,
          provider: cfg.provider,
          model: cfg.model,
          llmModel: llmModel || undefined,
        });
        if (!resp.success || !resp.data) {
          // apiFailureError keeps the status / error.code, which is what lets
          // ImageGeneratorPanel offer a token purchase for a 402 instead of
          // printing the refusal (see the ImageGenRunner contract).
          throw apiFailureError(
            resp,
            t('image.posts.generateError', { defaultValue: 'Failed to start generation' })
          );
        }
        return { done: false, jobId: resp.data.carousel_id };
      },

      poll: async (jobId) => {
        const statusResp = await carouselAPI.getStatus(jobId);
        if (!statusResp.success || !statusResp.data) {
          return {
            status: 'error',
            progress: { progress: 0, message: '' },
            error:
              statusResp.error ??
              t('image.posts.generateError', { defaultValue: 'Generation failed' }),
          };
        }
        const data = statusResp.data;
        setStatus(data);
        return {
          status:
            data.status === 'completed'
              ? 'completed'
              : data.status === 'failed'
                ? 'error'
                : 'processing',
          progress: { progress: data.progress, message: data.message },
          result: data.status === 'completed' ? data : undefined,
          error: data.status === 'failed' ? (data.error ?? undefined) : undefined,
        };
      },
    }),
    [topic, slideCount, formatPreset, llmModel, t]
  );

  const llmProviderOptions = useMemo(
    () =>
      LLM_PROVIDERS.map((provider) => ({
        value: provider.value,
        label: provider.label,
        disabled: provider.disabled,
      })),
    []
  );

  const handleCopyCaption = useCallback(async () => {
    if (!status?.caption) return;
    try {
      await navigator.clipboard.writeText(status.caption);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    } catch {
      /* clipboard unavailable — ignore */
    }
  }, [status]);

  const slides = status?.slides ?? [];
  const showResults = status?.status === 'completed' && slides.length > 0;

  return (
    <div className="space-y-6" data-testid="posts-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Images className="h-5 w-5" />
          <span>{t('image.posts.title', { defaultValue: 'Instagram Posts & Carousels' })}</span>
        </h3>
        <FreeOnDesktopBadge />
      </div>

      <p className="text-sm text-white/60">
        {t('image.posts.subtitle', {
          defaultValue:
            'Generate a single post or a carousel of up to 10 slides. One caption about the subject + per-slide images with optional text overlays.',
        })}
      </p>

      <ImageGeneratorPanel<CarouselStatus>
        config={config}
        onConfigChange={setConfig}
        runner={runner}
        onComplete={setStatus}
        // The carousel endpoint (CarouselGenerateRequest, extra=forbid) accepts
        // only provider + model today; prompt/styles/resolution/advanced are
        // derived server-side per slide. Hide what the backend does not honor so
        // we never show a control that silently does nothing (RULE #22). Threading
        // those through is a backend follow-up (CarouselGenerateRequest + orchestrator).
        showPrompt={false}
        showResolution={false}
        showStyles={false}
        showAdvanced={false}
        errorStage="carousel_generation"
        disabled={!topic.trim()}
        generateLabel={
          slideCount === 1
            ? t('image.posts.generatePost', { defaultValue: 'Generate Post' })
            : t('image.posts.generateCarousel', { defaultValue: 'Generate Carousel' })
        }
        data-testid="posts-generator"
        headerSlot={
          <>
            {/* Subject */}
            <div>
              <label className="form-label">
                {t('image.posts.subject', { defaultValue: 'Subject' })}
              </label>
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={t('image.posts.subjectPlaceholder', {
                  defaultValue: 'e.g. The rise and fall of the Roman Empire',
                })}
                rows={3}
                className="input w-full resize-none"
                data-testid="posts-subject-input"
              />
            </div>

            {/* Slide count + AI decide */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="form-label mb-0">
                  {t('image.posts.slideCount', { defaultValue: 'Slides' })}: {slideCount}
                </label>
                <button
                  type="button"
                  onClick={() => void handleSuggestCount()}
                  disabled={!topic.trim() || isSuggesting}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-purple-600/30 hover:bg-purple-600/50 text-purple-100 border border-purple-500/30 disabled:opacity-40 min-h-[36px]"
                  data-testid="posts-ai-count-btn"
                >
                  {isSuggesting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="h-3.5 w-3.5" />
                  )}
                  {t('image.posts.letAiDecide', { defaultValue: 'Let AI decide' })}
                </button>
              </div>
              <input
                type="range"
                min={MIN_SLIDES}
                max={MAX_SLIDES}
                value={slideCount}
                onChange={(e) => setSlideCount(Number(e.target.value))}
                className="w-full accent-purple-500"
                data-testid="posts-slide-count"
              />
              <div className="flex justify-between text-[11px] text-white/40 mt-1">
                <span>{t('image.posts.singlePost', { defaultValue: '1 (single post)' })}</span>
                <span>{t('image.posts.maxCarousel', { defaultValue: '10 (max carousel)' })}</span>
              </div>
              {suggestError && <p className="mt-2 text-xs text-red-300">{suggestError}</p>}
              {outline.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-white/60 bg-black/20 rounded-lg p-3 border border-white/10">
                  {outline.map((beat, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-purple-400">{i + 1}.</span>
                      <span>{beat}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Format preset */}
            <div>
              <label className="form-label">
                {t('image.posts.format', { defaultValue: 'Format' })}
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                {FORMAT_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => setFormatPreset(preset.key)}
                    className={`flex-1 min-h-[44px] px-3 py-2 rounded-lg border text-sm transition-colors ${
                      formatPreset === preset.key
                        ? 'bg-purple-600/40 border-purple-500/60 text-white'
                        : 'bg-black/20 border-white/10 text-white/70 hover:bg-black/30'
                    }`}
                  >
                    {t(preset.labelKey, { defaultValue: preset.fallback })}
                    <span className="block text-[11px] text-white/40">{preset.ratio}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Caption/plan LLM — optional override of the pinned default. */}
            <div className="space-y-3">
              <CompactSelect
                label={t('image.posts.llmProvider', { defaultValue: 'LLM Provider' })}
                options={llmProviderOptions}
                value={llmProvider}
                onChange={setLlmProvider}
                testId="posts-llm-provider-select"
              />

              {llmModelsError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-xs">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {t('image.posts.modelsError', {
                      defaultValue: "Couldn't load live model list — showing built-in defaults",
                    })}
                  </span>
                </div>
              )}

              <LLMModelSelector
                models={
                  llmModels.length === 0
                    ? [{ value: '', label: t('common.loading'), disabled: true }]
                    : llmModels
                }
                value={llmModel}
                onChange={setLlmModel}
                disabled={llmModels.length === 0}
                label={t('image.posts.captionModel', { defaultValue: 'Caption LLM' })}
                showFreeOnly={false}
                autoSelectCheapest={false}
              />
              <p className="text-[11px] text-white/40 mt-1">
                {t('image.posts.captionModelHint', {
                  defaultValue:
                    'Writes the caption + slide copy. Prefilled with the recommended model — change it if you prefer another.',
                })}
              </p>
            </div>
          </>
        }
      >
        {/* Results */}
        {showResults && status && (
          <div className="space-y-4" data-testid="posts-results">
            {/* Caption */}
            <div className="bg-black/30 rounded-lg p-4 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="form-label mb-0">
                  {t('image.posts.caption', { defaultValue: 'Caption' })}
                </span>
                <button
                  type="button"
                  onClick={() => void handleCopyCaption()}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 min-h-[32px]"
                >
                  {captionCopied ? (
                    <Check className="h-3.5 w-3.5 text-green-400" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  {captionCopied
                    ? t('image.posts.copied', { defaultValue: 'Copied' })
                    : t('image.posts.copy', { defaultValue: 'Copy' })}
                </button>
              </div>
              <p className="text-sm text-white/80 whitespace-pre-wrap">{status.caption}</p>
            </div>

            {/* Slides grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {slides.map((slide) => (
                <div
                  key={slide.index}
                  className="bg-black/30 rounded-lg overflow-hidden border border-white/10"
                >
                  <img
                    src={resolveCarouselUrl(slide.url)}
                    alt={`Slide ${slide.index + 1}`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-[11px] text-white/50">#{slide.index + 1}</span>
                    <a
                      href={resolveCarouselUrl(slide.url)}
                      download={`slide_${slide.index + 1}.png`}
                      className="text-white/60 hover:text-white"
                      title={t('image.posts.downloadSlide', { defaultValue: 'Download slide' })}
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Download all */}
            {status.zip_url && (
              <a
                href={resolveCarouselUrl(status.zip_url)}
                download={`carousel_${status.carousel_id}.zip`}
                className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10"
                data-testid="posts-download-zip"
              >
                <Download className="h-4 w-4" />
                {t('image.posts.downloadAll', { defaultValue: 'Download all (ZIP)' })}
              </a>
            )}
          </div>
        )}
      </ImageGeneratorPanel>
    </div>
  );
});

export default PostsTab;
